import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PremiumLock from '../components/PremiumLock';
import LyricsWithChords from '../components/LyricsWithChords';
import MiniMetronome from '../components/MiniMetronome';
import MiniDrums from '../components/MiniDrums';
import BackingTrack from '../components/BackingTrack';
import AudioBackingTrack from '../components/AudioBackingTrack';
import TabNotation from '../components/TabNotation';
import { toast } from 'sonner';
import { FileText, Filter, Crown, Music, Play, Pause, Square, CheckCircle, Guitar, Upload, Lock, RotateCcw, Repeat, Trash2, Save, Layers, Volume2, VolumeX, AlertCircle, X, Search, Timer, ChevronDown } from 'lucide-react';
import { getAllSongs as dbGetAll, saveSong as dbSave, deleteSong as dbDelete, savePreset as dbSavePreset, getPreset as dbGetPreset, deletePreset as dbDeletePreset } from '../utils/songdb';

const guessBackingStyles = (song) => {
  if (!song) return { drums: 'rock', bass: 'rootFifth', piano: 'block' };
  const techs = (song.techniques || []).join(' ').toLowerCase();
  const ts = (song.time_signature || '4/4').toString();
  const bpm = song.bpm || 90;
  const isWaltz = ts.startsWith('3') || ts.startsWith('6');

  let drums = 'rock';
  let bass = 'rootFifth';
  let piano = 'block';

  if (techs.includes('blues') || techs.includes('shuffle')) {
    drums = 'blues'; bass = 'walking'; piano = 'comping';
  } else if (techs.includes('jazz') || techs.includes('bossa') || techs.includes('swing')) {
    drums = 'jazz'; bass = 'walking'; piano = 'comping';
  } else if (techs.includes('funk') || techs.includes('16th')) {
    drums = 'funk'; bass = 'alternating'; piano = 'comping';
  } else if (techs.includes('country') || techs.includes('boom-chick') || techs.includes('bluegrass') || techs.includes('appalachian')) {
    drums = 'country'; bass = 'alternating'; piano = 'block';
  } else if (techs.includes('travis') || techs.includes('fingerpick')) {
    drums = 'folk'; bass = 'alternating'; piano = 'arpeggio';
  } else if (techs.includes('ballad') || techs.includes('slow') || bpm < 75) {
    drums = 'ballad'; bass = 'rootFifth'; piano = 'arpeggio';
  } else if (techs.includes('gospel') || techs.includes('dixieland')) {
    drums = 'pop'; bass = 'rootFifth'; piano = 'block';
  } else if (techs.includes('folk') || techs.includes('sing-along')) {
    drums = 'folk'; bass = 'alternating'; piano = 'arpeggio';
  } else if (techs.includes('mariachi') || techs.includes('rasgueado')) {
    drums = 'country'; bass = 'rootFifth'; piano = 'arpeggio';
  } else if (techs.includes('reggae') || techs.includes('skank')) {
    drums = 'pop'; bass = 'rootFifth'; piano = 'comping';
  } else if (techs.includes('metal') || techs.includes('palm mut')) {
    drums = 'rock'; bass = 'pedal'; piano = 'block';
  }

  if (isWaltz) {
    piano = 'waltz';
    if (drums === 'rock' || drums === 'pop' || drums === 'funk') drums = 'ballad';
  }

  // Pick a meter-appropriate drum pattern that actually exists in
  // MiniDrums / BackingTrack. 4/4 patterns are kept as-is; 3/4 / 6/8
  // / 12/8 songs get mapped to their compound counterparts.
  if (ts === '3/4') {
    drums = bpm < 75 ? 'ballad34' : 'waltz';
  } else if (ts === '6/8') {
    drums = 'ballad68';
  } else if (ts === '12/8') {
    drums = bpm < 70 ? 'ballad128' : 'slowBlues';
  }

  return { drums, bass, piano };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SongsPage = () => {
  const { t } = useTranslation();
  const { isPremium, isAuthenticated } = useAuth();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [openChapters, setOpenChapters] = useState({});
  const toggleChapter = (key) => setOpenChapters((o) => ({ ...o, [key]: !o[key] }));
  const [selectedSong, setSelectedSong] = useState(null);
  const [songLoading, setSongLoading] = useState(false);
  const [autoScroll, setAutoScroll] = useState(false);
  const [autoScrollSpeed, setAutoScrollSpeed] = useState(1); // multiplier 0.5x-2x
  const [showMetronome, setShowMetronome] = useState(false);
  const [showDrums, setShowDrums] = useState(false);
  const [showBacking, setShowBacking] = useState(false);
  const [viewMode, setViewMode] = useState('chords'); // 'chords' | 'tab'
  const lyricsScrollRef = useRef(null);
  const scrollRafRef = useRef(null);
  const scrollAccRef = useRef(0);
  const [practicedIds, setPracticedIds] = useState(new Set());
  const [savingId, setSavingId] = useState(null);

  // --- Import Player state ---
  const [importedFile, setImportedFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(null);
  const [isPlayerPlaying, setIsPlayerPlaying] = useState(false);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [loopA, setLoopA] = useState(null);
  const [loopB, setLoopB] = useState(null);
  const [loopEnabled, setLoopEnabled] = useState(false);

  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioUrlRef = useRef(null);

  // Library state (IndexedDB)
  const [savedSongs, setSavedSongs] = useState([]);
  const [activeSavedSongId, setActiveSavedSongId] = useState(null);
  const [dbError, setDbError] = useState(null);
  const FREE_LIMIT = 5;

  // --- Multitrack Session state ---
  const [mtTracks, setMtTracks] = useState([]);
  const [isMtPlaying, setIsMtPlaying] = useState(false);
  const [mtLoop, setMtLoop] = useState(false);
  const mtLoopRef = useRef(false);
  useEffect(() => { mtLoopRef.current = mtLoop; }, [mtLoop]);

  // Auto-scroll the lyrics container in time with the song's BPM. The
  // scroll rate is derived so that roughly one beat advances by one
  // sixteenth of a line height — slow enough to read, fast enough to
  // follow the music. The speed multiplier (0.5x-2x) lets the user
  // adjust to their reading pace.
  useEffect(() => {
    if (!autoScroll || !selectedSong) {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      return;
    }

    const el = lyricsScrollRef.current;
    if (!el) return;

    const bpm = selectedSong.bpm || 90;
    const ts = selectedSong.time_signature || '4/4';
    const beatsPerBar = parseInt(ts.split('/')[0], 10) || 4;

    const lyricsText = selectedSong.lyrics_with_chords || '';
    const verseHeaders = lyricsText.match(/^(verse|chorus|bridge|verso|coro|refrain|ritornello)/gim) || [];
    const verseCount = Math.max(verseHeaders.length, 1);

    const structure = selectedSong.structure || [];
    const barsPerCycle = structure.reduce((sum, s) => sum + (s.bars || 0), 0) || 16;
    const totalBeats = barsPerCycle * verseCount * beatsPerBar;
    const totalSeconds = totalBeats * (60 / bpm);

    const scrollDistance = el.scrollHeight - el.clientHeight;
    const pxPerSecond = totalSeconds > 0 && scrollDistance > 0
      ? (scrollDistance / totalSeconds) * autoScrollSpeed
      : (bpm / 60) * 6 * autoScrollSpeed;
    let lastTs = performance.now();

    const tick = (ts) => {
      const el = lyricsScrollRef.current;
      if (!el) {
        scrollRafRef.current = null;
        return;
      }
      const dt = (ts - lastTs) / 1000;
      lastTs = ts;
      scrollAccRef.current += pxPerSecond * dt;
      const whole = Math.floor(scrollAccRef.current);
      if (whole > 0) {
        el.scrollTop += whole;
        scrollAccRef.current -= whole;
      }
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
        setAutoScroll(false);
        return;
      }
      scrollRafRef.current = requestAnimationFrame(tick);
    };

    scrollAccRef.current = 0;
    scrollRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [autoScroll, autoScrollSpeed, selectedSong]);

  // Reset playback state whenever a new song is opened or closed
  useEffect(() => {
    setAutoScroll(false);
    setAutoScrollSpeed(1);
    setShowMetronome(false);
    setShowDrums(false);
    setShowBacking(false);
    setViewMode('chords');
    scrollAccRef.current = 0;
    if (lyricsScrollRef.current) lyricsScrollRef.current.scrollTop = 0;
  }, [selectedSong?.id]);

  const [mtMasterVolume, setMtMasterVolume] = useState(80);
  const MT_MAX_PRO = 5;
  const MT_MAX_FREE = 2;

  const mtCtxRef = useRef(null);
  const mtSourcesRef = useRef([]);
  const mtGainsRef = useRef({});
  const mtMasterGainRef = useRef(null);
  const mtFileInputRef = useRef(null);

  // Multitrack timeline
  const [mtElapsed, setMtElapsed] = useState(0);
  const [mtSessionDuration, setMtSessionDuration] = useState(0);
  const mtStartAtRef = useRef(0);
  const mtSessionDurRef = useRef(0);
  const mtTimeRafRef = useRef(null);
  const mtFallbackTimerRef = useRef(null);

  // Library picker state
  const [showLibPicker, setShowLibPicker] = useState(false);
  const [libPickerSel, setLibPickerSel] = useState(new Set());
  const [libSearchQ, setLibSearchQ] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  useEffect(() => {
    fetchSongs();
    if (isAuthenticated) {
      fetchProgress();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!focusId || songs.length === 0 || selectedSong) return;
    const target = songs.find(s => s.id === focusId);
    if (target) {
      openSong(target);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, songs]);

  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API}/songs`);
      setSongs(response.data);
    } catch (error) {
      console.error('Error fetching songs:', error);
    } finally {
      setLoading(false);
    }
  };

  // /songs sometimes returns objects without lyrics_with_chords (depending
  // on backend version / projection). When the user opens a song, always
  // refetch the single-song endpoint to make sure the modal has the full
  // chord chart.
  const openSong = async (song) => {
    setSelectedSong(song);
    if (song.lyrics_with_chords) return;
    setSongLoading(true);
    try {
      const { data } = await axios.get(`${API}/songs/${song.id}`);
      setSelectedSong(data);
    } catch (error) {
      console.error('Error fetching song:', error);
    } finally {
      setSongLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress?item_type=song`);
      const practiced = new Set(
        response.data.filter(p => p.status === 'completed').map(p => p.item_id)
      );
      setPracticedIds(practiced);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  // Cleanup imported audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      if (mtTimeRafRef.current) cancelAnimationFrame(mtTimeRafRef.current);
      if (mtFallbackTimerRef.current) clearTimeout(mtFallbackTimerRef.current);
      mtSourcesRef.current.forEach(s => { try { s.stop(); } catch (_) {} });
      if (mtCtxRef.current) { try { mtCtxRef.current.close(); } catch (_) {} }
    };
  }, []);

  // Load saved songs from IndexedDB on mount
  useEffect(() => {
    const loadLibrary = async () => {
      try {
        const songs = await dbGetAll();
        setSavedSongs(songs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } catch (e) {
        setDbError(t('songs.dbUnavailable'));
      }
    };
    loadLibrary();
  }, []);

  // --- Library handlers ---
  const saveToLibrary = async () => {
    if (!importedFile) return;
    if (!isPremium && savedSongs.length >= FREE_LIMIT) {
      toast.error(t('songs.limitReached', { count: FREE_LIMIT }));
      return;
    }
    try {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const entry = { id, name: importedFile.name, type: importedFile.type, size: importedFile.size, createdAt: new Date().toISOString(), blob: importedFile };
      await dbSave(entry);
      setSavedSongs(prev => [entry, ...prev]);
      setActiveSavedSongId(id);
      toast.success(t('songs.saved'));
    } catch (e) {
      toast.error(t('songs.saveError'));
    }
  };

  const loadSavedSong = async (song) => {
    // Stop current playback and revoke old URL
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.playbackRate = 1.0; }
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    setIsPlayerPlaying(false);
    setPlayerTime(0);
    setPlayerDuration(0);
    setPlaybackRate(1.0);
    setLoopA(null);
    setLoopB(null);
    setLoopEnabled(false);
    setAudioError(null);

    const url = URL.createObjectURL(song.blob);
    audioUrlRef.current = url;
    setImportedFile({ name: song.name, size: song.size, type: song.type });
    setAudioUrl(url);
    setActiveSavedSongId(song.id);

    // Load preset (PRO)
    if (isPremium) {
      try {
        const preset = await dbGetPreset(song.id);
        if (preset) {
          setPlaybackRate(preset.speed || 1.0);
          setLoopA(preset.loopA ?? null);
          setLoopB(preset.loopB ?? null);
          setLoopEnabled(preset.loopEnabled || false);
          // Apply playbackRate after audio loads
          setTimeout(() => { if (audioRef.current) audioRef.current.playbackRate = preset.speed || 1.0; }, 100);
        }
      } catch (_) {}
    }
  };

  const deleteSavedSong = async (id) => {
    try {
      await dbDelete(id);
      await dbDeletePreset(id).catch(() => {});
      setSavedSongs(prev => prev.filter(s => s.id !== id));
      if (activeSavedSongId === id) setActiveSavedSongId(null);
      toast.success(t('songs.removed'));
    } catch (e) {
      toast.error(t('songs.removeError'));
    }
  };

  const handleSavePreset = async () => {
    if (!isPremium || !activeSavedSongId) return;
    try {
      await dbSavePreset({ songId: activeSavedSongId, speed: playbackRate, loopA, loopB, loopEnabled });
      toast.success(t('songs.presetSaved'));
    } catch (_) {
      toast.error(t('songs.presetSaveError'));
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }); }
    catch (_) { return ''; }
  };

  // --- Import Player handlers ---
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.playbackRate = 1.0; }
    setIsPlayerPlaying(false);
    setPlayerTime(0);
    setPlayerDuration(0);
    setPlaybackRate(1.0);
    setLoopA(null);
    setLoopB(null);
    setLoopEnabled(false);
    setAudioError(null);
    setActiveSavedSongId(null);
    const url = URL.createObjectURL(file);
    audioUrlRef.current = url;
    setImportedFile(file);
    setAudioUrl(url);
    e.target.value = '';
  };

  const togglePlayerPlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (isPlayerPlaying) {
      audioRef.current.pause();
      setIsPlayerPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlayerPlaying(true);
    }
  };

  const handleSeek = (val) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = val[0];
    setPlayerTime(val[0]);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    setPlayerTime(t);
    if (loopEnabled && loopA !== null && loopB !== null && loopB > loopA && t >= loopB) {
      audioRef.current.currentTime = loopA;
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setPlayerDuration(audioRef.current.duration);
  };

  const handleAudioError = () => { setAudioError(t('songs.unsupportedFormat')); };
  const handleEnded = () => { setIsPlayerPlaying(false); };

  const changeSpeed = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
  };

  const fmtTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const fmtSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const speeds = [0.5, 0.75, 1.0, 1.25, 1.5];

  // --- Multitrack Session handlers ---
  const readFileBuffer = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error);
      r.readAsArrayBuffer(file);
    });

  const ensureMtCtx = () => {
    if (!mtCtxRef.current) {
      mtCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (mtCtxRef.current.state === 'suspended') mtCtxRef.current.resume();
    return mtCtxRef.current;
  };

  const handleMultiFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';

    const limit = isPremium ? MT_MAX_PRO : MT_MAX_FREE;
    const currentCount = mtTracks.length;
    const available = limit - currentCount;
    if (available <= 0) {
      toast.error(isPremium
        ? t('songs.mtLimitPro', { max: MT_MAX_PRO })
        : t('songs.mtLimitFree', { free: MT_MAX_FREE, pro: MT_MAX_PRO }));
      return;
    }

    const toAdd = files.slice(0, available);
    if (files.length > available) {
      toast(t('songs.mtUploadedSome', { loaded: toAdd.length, total: files.length, limit }));
    }

    // Stop current playback before adding
    if (isMtPlaying) stopAllMtTracks(false);

    const ctx = ensureMtCtx();
    const newTracks = [];

    for (const file of toAdd) {
      const id = `mt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      try {
        const ab = await readFileBuffer(file);
        const buffer = await ctx.decodeAudioData(ab);
        newTracks.push({ id, name: file.name, buffer, status: 'ready', volume: 80, muted: false, solo: false });
      } catch (_) {
        newTracks.push({ id, name: file.name, buffer: null, status: 'unsupported', volume: 80, muted: false, solo: false });
      }
    }

    setMtTracks(prev => [...prev, ...newTracks]);
  };

  const openLibPicker = () => {
    setLibPickerSel(new Set());
    setLibSearchQ('');
    setShowLibPicker(true);
  };

  const toggleLibPickerItem = (id) => {
    setLibPickerSel(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addFromLibrary = async () => {
    if (libPickerSel.size === 0) return;

    const limit = isPremium ? MT_MAX_PRO : MT_MAX_FREE;
    const available = limit - mtTracks.length;
    if (available <= 0) {
      toast.error(isPremium
        ? t('songs.mtLimitPro', { max: MT_MAX_PRO })
        : t('songs.mtLimitFree', { free: MT_MAX_FREE, pro: MT_MAX_PRO }));
      return;
    }

    const selected = savedSongs.filter(s => libPickerSel.has(s.id));
    const toAdd = selected.slice(0, available);

    if (isMtPlaying) stopAllMtTracks(false);
    const ctx = ensureMtCtx();
    const newTracks = [];

    for (const song of toAdd) {
      const id = `mt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      try {
        const ab = await song.blob.arrayBuffer();
        const buffer = await ctx.decodeAudioData(ab);
        newTracks.push({ id, name: song.name, buffer, status: 'ready', volume: 80, muted: false, solo: false });
      } catch (_) {
        newTracks.push({ id, name: song.name, buffer: null, status: 'unsupported', volume: 80, muted: false, solo: false });
      }
    }

    setMtTracks(prev => [...prev, ...newTracks]);
    setShowLibPicker(false);
    setLibPickerSel(new Set());
    if (toAdd.length < selected.length) {
      toast(`Aggiunte ${toAdd.length} di ${selected.length} tracce (limite: ${limit})`);
    }
  };

  const removeMtTrack = (id) => {
    if (isMtPlaying) stopAllMtTracks(false);
    setMtTracks(prev => prev.filter(t => t.id !== id));
  };

  const playAllMtTracks = (startOffset = 0) => {
    const valid = mtTracks.filter(t => t.status === 'ready');
    if (!valid.length) { toast.error(t('songs.mtInvalidTrack')); return; }

    if (isMtPlaying) stopAllMtTracks(false);
    const ctx = ensureMtCtx();
    const safeOffset = Math.max(0, startOffset);

    if (!mtMasterGainRef.current || mtMasterGainRef.current.context !== ctx) {
      // Disconnect the previous master gain so it does not stay attached
      // to a stale destination after the AudioContext is recreated.
      if (mtMasterGainRef.current) {
        try { mtMasterGainRef.current.disconnect(); } catch (_) {}
      }
      mtMasterGainRef.current = ctx.createGain();
      mtMasterGainRef.current.connect(ctx.destination);
    }
    mtMasterGainRef.current.gain.value = mtMasterVolume / 100;

    const anySolo = mtTracks.some(t => t.solo);
    const startAt = ctx.currentTime + 0.05;
    const sources = [];
    const gains = {};

    let maxDur = 0;
    for (const track of valid) {
      if (track.buffer.duration > maxDur) maxDur = track.buffer.duration;
      // Skip tracks shorter than the requested offset — they would
      // start past their end and never play.
      if (safeOffset >= track.buffer.duration) continue;
      const source = ctx.createBufferSource();
      source.buffer = track.buffer;
      const gain = ctx.createGain();
      let vol = track.volume / 100;
      if (track.muted) vol = 0;
      else if (anySolo && !track.solo) vol = 0;
      gain.gain.value = vol;
      source.connect(gain);
      gain.connect(mtMasterGainRef.current);
      source.start(startAt, safeOffset);
      sources.push(source);
      gains[track.id] = gain;
    }

    if (!sources.length) {
      toast.error(t('songs.mtSeekOutOfRange'));
      return;
    }

    mtSourcesRef.current = sources;
    mtGainsRef.current = gains;

    // Timeline: store the virtual "session zero" so that
    // ctx.currentTime - mtStartAtRef gives the position inside the song,
    // regardless of where we seeked from.
    mtStartAtRef.current = startAt - safeOffset;
    mtSessionDurRef.current = maxDur;
    setMtSessionDuration(maxDur);
    setMtElapsed(safeOffset);
    const updateTimeline = () => {
      if (!mtCtxRef.current) return;
      const el = mtCtxRef.current.currentTime - mtStartAtRef.current;
      setMtElapsed(Math.max(0, Math.min(el, mtSessionDurRef.current)));
      mtTimeRafRef.current = requestAnimationFrame(updateTimeline);
    };
    mtTimeRafRef.current = requestAnimationFrame(updateTimeline);

    setMtTracks(prev => prev.map(t => t.status === 'ready' ? { ...t, status: 'playing' } : t));
    setIsMtPlaying(true);

    let ended = 0;
    let finalized = false;
    const finalize = () => {
      if (finalized) return;
      finalized = true;
      if (mtTimeRafRef.current) cancelAnimationFrame(mtTimeRafRef.current);
      mtTimeRafRef.current = null;
      if (mtFallbackTimerRef.current) {
        clearTimeout(mtFallbackTimerRef.current);
        mtFallbackTimerRef.current = null;
      }
      if (mtLoopRef.current) {
        // Loop mode: reset track statuses then restart playback on the next
        // tick so React has time to apply the state update.
        setMtTracks(prev => prev.map(t => t.status === 'playing' ? { ...t, status: 'ready' } : t));
        setTimeout(() => playAllMtTracks(), 0);
        return;
      }
      setIsMtPlaying(false);
      setMtTracks(prev => prev.map(t => t.status === 'playing' ? { ...t, status: 'ready' } : t));
    };
    sources.forEach(s => {
      s.onended = () => {
        ended++;
        if (ended >= sources.length) finalize();
      };
    });
    // Safety net: if any onended fails to fire (decode quirks, context
    // suspension, browser bugs) the UI would stay stuck in "playing".
    // Force-finalize 500ms after the longest track is expected to end.
    const remaining = Math.max(0, maxDur - safeOffset);
    if (mtFallbackTimerRef.current) clearTimeout(mtFallbackTimerRef.current);
    mtFallbackTimerRef.current = setTimeout(finalize, (remaining + 0.5) * 1000);
  };

  const seekMt = (time) => {
    if (!mtSessionDurRef.current) return;
    const clamped = Math.max(0, Math.min(time, mtSessionDurRef.current));
    if (isMtPlaying) {
      stopAllMtTracks(false);
      // Restart on next tick so React commits the stopped state first
      setTimeout(() => playAllMtTracks(clamped), 0);
    } else {
      setMtElapsed(clamped);
    }
  };

  const handleMtTimelineClick = (e) => {
    if (!mtSessionDurRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    seekMt(ratio * mtSessionDurRef.current);
  };

  const stopAllMtTracks = (updateState = true) => {
    mtSourcesRef.current.forEach(s => { try { s.stop(); } catch (_) {} });
    mtSourcesRef.current = [];
    mtGainsRef.current = {};
    if (mtTimeRafRef.current) cancelAnimationFrame(mtTimeRafRef.current);
    mtTimeRafRef.current = null;
    if (mtFallbackTimerRef.current) {
      clearTimeout(mtFallbackTimerRef.current);
      mtFallbackTimerRef.current = null;
    }
    if (updateState) {
      setIsMtPlaying(false);
      setMtElapsed(0);
      setMtTracks(prev => prev.map(t => t.status === 'playing' ? { ...t, status: 'ready' } : t));
    }
  };

  const clearMtSession = () => {
    stopAllMtTracks(false);
    setMtTracks([]);
    setIsMtPlaying(false);
    setMtElapsed(0);
    setMtSessionDuration(0);
  };

  const updateMtTrackProp = (id, key, value) => {
    setMtTracks(prev => prev.map(t => t.id === id ? { ...t, [key]: value } : t));
  };

  const toggleMtMute = (id) => updateMtTrackProp(id, 'muted', !mtTracks.find(t => t.id === id)?.muted);
  const toggleMtSolo = (id) => updateMtTrackProp(id, 'solo', !mtTracks.find(t => t.id === id)?.solo);

  // Apply gain values during playback when mute/solo/volume changes
  useEffect(() => {
    if (!isMtPlaying) return;
    const anySolo = mtTracks.some(t => t.solo);
    for (const t of mtTracks) {
      const g = mtGainsRef.current[t.id];
      if (!g) continue;
      let vol = t.volume / 100;
      if (t.muted) vol = 0;
      else if (anySolo && !t.solo) vol = 0;
      g.gain.value = vol;
    }
  }, [mtTracks, isMtPlaying]);

  // Apply master volume
  useEffect(() => {
    if (mtMasterGainRef.current) mtMasterGainRef.current.gain.value = mtMasterVolume / 100;
  }, [mtMasterVolume]);

  const markPracticed = async (songId) => {
    if (!isAuthenticated) {
      toast.error(t('songs.practiceLogin'));
      return;
    }
    
    setSavingId(songId);
    try {
      await axios.post(`${API}/progress?item_type=song&item_id=${songId}&status=completed`);
      setPracticedIds(prev => new Set([...prev, songId]));
      toast.success(t('songs.practiced'));
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error(t('songs.practiceError'));
    } finally {
      setSavingId(null);
    }
  };

  const filteredSongs = songs.filter(song => {
    if (selectedDifficulty !== 'all' && song.difficulty !== selectedDifficulty) return false;
    return true;
  });

  const difficulties = ['beginner', 'intermediate', 'advanced', 'master'];

  // Chapter grouping by difficulty (mirrors TheoryPage / ExercisesPage).
  // Only active when no specific difficulty filter is selected.
  const DIFF_ORDER = ['beginner', 'intermediate', 'advanced', 'master'];
  const groupedChapters = selectedDifficulty === 'all'
    ? DIFF_ORDER
        .map(d => ({ difficulty: d, items: filteredSongs.filter(s => s.difficulty === d) }))
        .filter(c => c.items.length > 0)
    : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="songs-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-400 flex items-center justify-center">
              <FileText className="w-8 h-8 text-black" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">
              {t('songs.title')}
            </h1>
            <Button
              onClick={() => { setLoading(true); fetchSongs(); }}
              disabled={loading}
              variant="outline"
              size="sm"
              className="ml-auto border-white/15 text-zinc-300 hover:bg-white/5"
              data-testid="songs-refresh"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>
          </div>
          <p className="text-zinc-400 ml-[74px]">Copyright-free songs to practice</p>
        </motion.div>

        {/* Import Brano */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-6 mb-8"
          data-testid="import-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-400" />
              Importa Brano
            </h3>
            <Button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileInputRef.current?.click(); }}
              size="sm"
              className="bg-gradient-to-r from-green-500 to-emerald-400 text-black font-semibold"
              data-testid="import-file-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Seleziona File
            </Button>
            <input
              ref={fileInputRef}
              id="audioImportSingle"
              type="file"
              accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,audio/ogg,audio/flac"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="import-file-input"
            />
          </div>

          {!importedFile && (
            <p className="text-zinc-500 text-sm">Importa un file audio locale (mp3, wav, m4a) per usare il player da studio.</p>
          )}

          {importedFile && (
            <div>
              {/* File info */}
              <div className="text-sm text-zinc-400 mb-4" data-testid="import-file-info">
                {importedFile.name} <span className="text-zinc-600">({fmtSize(importedFile.size)})</span>
              </div>

              {audioError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-300 text-sm" data-testid="audio-error">
                  {audioError}
                </div>
              )}

              {/* Hidden audio element */}
              <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onError={handleAudioError}
                onEnded={handleEnded}
                preload="metadata"
              />

              {!audioError && (
                <div className="space-y-5">
                  {/* Play/Pause + Time */}
                  <div className="flex items-center gap-4">
                    <Button
                      onClick={togglePlayerPlay}
                      size="sm"
                      className={isPlayerPlaying
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-gradient-to-r from-green-500 to-emerald-400 text-black'
                      }
                      data-testid="player-play-btn"
                    >
                      {isPlayerPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
                    </Button>
                    <span className="font-mono text-sm text-zinc-400" data-testid="player-time">
                      {fmtTime(playerTime)} / {fmtTime(playerDuration)}
                    </span>
                  </div>

                  {/* Seek bar */}
                  <Slider
                    value={[playerTime]}
                    max={playerDuration || 1}
                    onValueChange={handleSeek}
                    step={0.1}
                    className="w-full"
                    data-testid="player-seek"
                  />

                  {/* Speed Control (PRO) */}
                  <div className="relative">
                    {!isPremium && (
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-lg">
                        <Lock className="w-4 h-4 text-zinc-500 mr-1" />
                        <span className="text-zinc-400 text-xs">Solo PRO</span>
                      </div>
                    )}
                    <div className={!isPremium ? 'pointer-events-none' : ''}>
                      <label className="text-sm text-zinc-400 mb-2 block">
                        Velocità: <span className="text-white font-mono font-bold">{playbackRate}x</span>
                      </label>
                      <div className="flex gap-2">
                        {speeds.map(r => (
                          <Button
                            key={r}
                            onClick={() => changeSpeed(r)}
                            size="sm"
                            className={`flex-1 ${playbackRate === r
                              ? 'bg-green-500 text-black'
                              : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                            }`}
                            data-testid={`speed-${r}`}
                          >
                            {r}x
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Loop A/B (PRO) */}
                  <div className="relative">
                    {!isPremium && (
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-lg">
                        <Lock className="w-4 h-4 text-zinc-500 mr-1" />
                        <span className="text-zinc-400 text-xs">Solo PRO</span>
                      </div>
                    )}
                    <div className={!isPremium ? 'pointer-events-none' : ''}>
                      <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                        <Repeat className="w-4 h-4" /> Loop A/B
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => setLoopA(playerTime)}
                          size="sm"
                          className={`${loopA !== null ? 'bg-amber/20 text-amber border border-amber/30' : 'bg-white/5 text-zinc-400'}`}
                          data-testid="loop-set-a"
                        >
                          A {loopA !== null ? fmtTime(loopA) : ''}
                        </Button>
                        <Button
                          onClick={() => setLoopB(playerTime)}
                          size="sm"
                          className={`${loopB !== null ? 'bg-amber/20 text-amber border border-amber/30' : 'bg-white/5 text-zinc-400'}`}
                          data-testid="loop-set-b"
                        >
                          B {loopB !== null ? fmtTime(loopB) : ''}
                        </Button>
                        <Button
                          onClick={() => setLoopEnabled(!loopEnabled)}
                          size="sm"
                          disabled={loopA === null || loopB === null}
                          className={loopEnabled
                            ? 'bg-green-500 text-black'
                            : 'bg-white/5 text-zinc-400'
                          }
                          data-testid="loop-toggle"
                        >
                          {loopEnabled ? t('songs.loopOn') : t('songs.loopOff')}
                        </Button>
                        <Button
                          onClick={() => { setLoopA(null); setLoopB(null); setLoopEnabled(false); }}
                          size="sm"
                          variant="ghost"
                          className="text-zinc-500 hover:text-white"
                          data-testid="loop-reset"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Reset
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Save to Library + Save Preset */}
                  <div className="flex flex-wrap gap-3 pt-2 border-t border-white/5">
                    {!activeSavedSongId && (
                      <Button
                        onClick={saveToLibrary}
                        size="sm"
                        disabled={!isPremium && savedSongs.length >= FREE_LIMIT}
                        className="bg-white/5 text-zinc-300 hover:bg-white/10"
                        data-testid="save-to-library-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Salva in libreria
                        {!isPremium && <span className="ml-2 text-zinc-500 text-xs">({savedSongs.length}/{FREE_LIMIT})</span>}
                      </Button>
                    )}
                    {activeSavedSongId && (
                      <span className="flex items-center text-green-400 text-xs"><CheckCircle className="w-3 h-3 mr-1" /> In libreria</span>
                    )}

                    {/* Save Preset (PRO) */}
                    <div className="relative">
                      {!isPremium && (
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-lg">
                          <Lock className="w-3 h-3 text-zinc-500 mr-1" />
                          <span className="text-zinc-400 text-[10px]">PRO</span>
                        </div>
                      )}
                      <Button
                        onClick={handleSavePreset}
                        size="sm"
                        disabled={!activeSavedSongId || !isPremium}
                        className="bg-white/5 text-zinc-300 hover:bg-white/10"
                        data-testid="save-preset-btn"
                      >
                        <Save className="w-3 h-3 mr-1" /> Salva Preset
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Brani Salvati */}
        {(savedSongs.length > 0 || dbError) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07 }}
            className="bg-[#121212] border border-white/5 rounded-2xl p-6 mb-8"
            data-testid="library-section"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <Music className="w-5 h-5 text-green-400" />
                Brani Salvati
                <span className="text-zinc-500 text-sm font-normal">
                  ({savedSongs.length}{!isPremium ? `/${FREE_LIMIT}` : ''})
                </span>
              </h3>
            </div>

            {dbError && (
              <p className="text-amber text-sm mb-4" data-testid="db-error">{dbError}</p>
            )}

            {!isPremium && savedSongs.length >= FREE_LIMIT && (
              <div className="bg-amber/10 border border-amber/20 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="free-limit-msg">
                <Crown className="w-4 h-4 text-amber flex-shrink-0" />
                <span className="text-amber text-sm">Limite raggiunto. Passa a PRO per brani illimitati.</span>
              </div>
            )}

            <div className="space-y-2">
              {savedSongs.map((s) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                    activeSavedSongId === s.id ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/3 hover:bg-white/5'
                  }`}
                  data-testid={`saved-song-${s.id}`}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-white text-sm font-medium truncate">{s.name}</p>
                    <p className="text-zinc-500 text-xs">{fmtDate(s.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => loadSavedSong(s)}
                      size="sm"
                      className={activeSavedSongId === s.id
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                      }
                      data-testid={`load-song-${s.id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      {activeSavedSongId === s.id ? t('songs.loaded') : t('songs.load')}
                    </Button>
                    <Button
                      onClick={() => deleteSavedSong(s.id)}
                      size="sm"
                      variant="ghost"
                      className="text-zinc-500 hover:text-red-400"
                      data-testid={`delete-song-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Multitrack Session */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.09 }}
          className="bg-[#121212] border border-purple-500/20 rounded-2xl p-6 mb-8"
          data-testid="mt-session"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-400" />
              Multitrack Session
              <span className="text-zinc-500 text-sm font-normal">
                ({mtTracks.length}/{isPremium ? MT_MAX_PRO : MT_MAX_FREE})
              </span>
            </h3>
            <div className="flex items-center gap-2">
              <Button
                onClick={openLibPicker}
                size="sm"
                disabled={savedSongs.length === 0 || mtTracks.length >= (isPremium ? MT_MAX_PRO : MT_MAX_FREE)}
                className="bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-40"
                data-testid="mt-add-from-lib"
              >
                <Music className="w-4 h-4 mr-2" /> Da Libreria
              </Button>
              <Button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); mtFileInputRef.current?.click(); }}
                size="sm"
                disabled={mtTracks.length >= (isPremium ? MT_MAX_PRO : MT_MAX_FREE)}
                className="bg-white/5 text-zinc-300 hover:bg-white/10 disabled:opacity-40"
                data-testid="mt-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Importa File
              </Button>
              <input
                ref={mtFileInputRef}
                id="audioImportMulti"
                type="file"
                accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,audio/ogg,audio/flac"
                multiple
                className="hidden"
                onChange={handleMultiFileSelect}
                data-testid="mt-file-input"
              />
              {mtTracks.length > 0 && (
                <>
                  <Button
                    onClick={playAllMtTracks}
                    size="sm"
                    disabled={isMtPlaying || !mtTracks.some(t => t.status === 'ready')}
                    className="bg-green-500 hover:bg-green-600 text-black disabled:opacity-40"
                    data-testid="mt-play-all"
                  >
                    <Play className="w-4 h-4 mr-1" /> Play All
                  </Button>
                  {isMtPlaying && (
                    <Button
                      onClick={() => stopAllMtTracks()}
                      size="sm"
                      className="bg-red-500 hover:bg-red-600 text-white"
                      data-testid="mt-stop"
                    >
                      <Square className="w-4 h-4 mr-1 fill-current" /> Stop
                    </Button>
                  )}
                  <Button
                    onClick={clearMtSession}
                    size="sm"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white"
                    data-testid="mt-clear"
                  >
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </>
              )}
            </div>
          </div>

          {!isPremium && mtTracks.length >= MT_MAX_FREE && (
            <div className="bg-amber/10 border border-amber/20 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="mt-free-limit">
              <Crown className="w-4 h-4 text-amber flex-shrink-0" />
              <span className="text-amber text-sm">Limite {MT_MAX_FREE} tracce raggiunto. Passa a PRO per {MT_MAX_PRO} tracce.</span>
            </div>
          )}

          {mtTracks.length === 0 && (
            <p className="text-zinc-500 text-sm">Importa più file audio per creare una sessione multitrack con mixer completo.</p>
          )}

          {/* Track List */}
          {mtTracks.length > 0 && (
            <div className="space-y-2 mb-4">
              {mtTracks.map((track, idx) => (
                <div
                  key={track.id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    track.status === 'unsupported' ? 'bg-red-500/5 border border-red-500/20' :
                    track.status === 'playing' ? 'bg-purple-500/10 border border-purple-500/20' :
                    'bg-white/3'
                  }`}
                  data-testid={`mt-track-${track.id}`}
                >
                  <span className="text-purple-400 text-xs font-mono w-5">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{track.name}</p>
                    {track.status === 'unsupported' && (
                      <p className="text-red-400 text-xs flex items-center gap-1 mt-0.5">
                        <AlertCircle className="w-3 h-3" /> Formato non supportato
                      </p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    track.status === 'playing' ? 'bg-green-500/20 text-green-400' :
                    track.status === 'unsupported' ? 'bg-red-500/20 text-red-400' :
                    'bg-white/5 text-zinc-500'
                  }`} data-testid={`mt-status-${track.id}`}>
                    {track.status === 'playing' ? t('songs.statusPlaying') : track.status === 'unsupported' ? t('songs.statusError') : t('songs.statusReady')}
                  </span>
                  <Button
                    onClick={() => removeMtTrack(track.id)}
                    size="sm"
                    variant="ghost"
                    className="text-zinc-500 hover:text-red-400"
                    data-testid={`mt-remove-${track.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Mixer — only for valid tracks */}
          {mtTracks.some(t => t.status !== 'unsupported') && mtTracks.length > 0 && (
            <div className="border-t border-white/5 pt-4 space-y-3" data-testid="mt-mixer">
              {/* Timeline */}
              {(isMtPlaying || mtSessionDuration > 0) && (
                <div className="flex items-center gap-3 pb-3 border-b border-white/5" data-testid="mt-timeline">
                  <span className="font-mono text-xs text-zinc-400 w-12">{fmtTime(mtElapsed)}</span>
                  <div
                    role="slider"
                    aria-label={t('songs.timelineAria')}
                    aria-valuemin={0}
                    aria-valuemax={mtSessionDuration}
                    aria-valuenow={mtElapsed}
                    tabIndex={0}
                    onClick={handleMtTimelineClick}
                    className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden cursor-pointer hover:bg-white/10"
                    data-testid="mt-timeline-bar"
                  >
                    <div
                      className="h-full bg-purple-500 rounded-full transition-[width] duration-100 pointer-events-none"
                      style={{ width: `${mtSessionDuration > 0 ? (mtElapsed / mtSessionDuration * 100) : 0}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs text-zinc-400 w-12 text-right">{fmtTime(mtSessionDuration)}</span>
                </div>
              )}
              {/* Master Volume */}
              <div className="flex items-center gap-3 pb-3 border-b border-white/5" data-testid="mt-master-vol">
                <Volume2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium w-16">Master</span>
                <Slider
                  value={[mtMasterVolume]}
                  onValueChange={(v) => setMtMasterVolume(v[0])}
                  min={0} max={100} step={1}
                  className="flex-1"
                  data-testid="mt-master-slider"
                />
                <span className="text-zinc-400 text-xs font-mono w-10 text-right">{mtMasterVolume}%</span>
                <button
                  onClick={() => setMtLoop(v => !v)}
                  className={`ml-2 w-9 h-7 rounded text-xs font-bold flex items-center justify-center transition-all ${
                    mtLoop ? 'bg-purple-500 text-white' : 'bg-white/5 text-zinc-500 hover:bg-white/10'
                  }`}
                  title={mtLoop ? t('songs.loopActive') : t('songs.loopInactive')}
                  data-testid="mt-loop-toggle"
                >
                  <Repeat className="w-4 h-4" />
                </button>
              </div>

              {/* Per-track mixer */}
              {mtTracks.filter(t => t.status !== 'unsupported').map((track, idx) => (
                <div key={track.id} className="flex items-center gap-2" data-testid={`mt-mixer-${track.id}`}>
                  <span className="text-white text-sm w-20 truncate" title={track.name}>Track {idx + 1}</span>
                  <button
                    onClick={() => toggleMtMute(track.id)}
                    className={`w-7 h-7 rounded text-xs font-bold flex items-center justify-center transition-all ${
                      track.muted ? 'bg-red-500 text-white' : 'bg-white/5 text-zinc-500 hover:bg-white/10'
                    }`}
                    data-testid={`mt-mute-${track.id}`}
                  >
                    {track.muted ? <VolumeX className="w-3.5 h-3.5" /> : 'M'}
                  </button>
                  <button
                    onClick={() => toggleMtSolo(track.id)}
                    className={`w-7 h-7 rounded text-xs font-bold flex items-center justify-center transition-all ${
                      track.solo ? 'bg-amber text-black' : 'bg-white/5 text-zinc-500 hover:bg-white/10'
                    }`}
                    data-testid={`mt-solo-${track.id}`}
                  >
                    S
                  </button>
                  <Slider
                    value={[track.volume]}
                    onValueChange={(v) => updateMtTrackProp(track.id, 'volume', v[0])}
                    min={0} max={100} step={1}
                    className="flex-1"
                    data-testid={`mt-vol-${track.id}`}
                  />
                  <span className="text-zinc-400 text-xs font-mono w-10 text-right">{track.volume}%</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-4 mb-8"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <span className="text-zinc-400 text-sm">{t('common.filter')}:</span>
          </div>
          
          <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="difficulty-filter">
              <SelectValue placeholder={t('songs.difficulty')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {difficulties.map(d => (
                <SelectItem key={d} value={d}>{t(`levels.${d}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Songs Grid */}
        {!loading && (() => {
          const renderCard = (song, index) => (
            <motion.div
              key={song.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="relative bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-green-500/30 transition-all group"
              data-testid={`song-card-${song.id}`}
            >
              {song.is_premium && !isPremium && <PremiumLock />}

              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-400/20 flex items-center justify-center">
                  <Music className="w-8 h-8 text-green-400" />
                </div>
                {song.is_premium && <Crown className="w-5 h-5 text-amber" />}
              </div>

              <h3 className="font-heading text-xl font-bold text-white mb-1">
                {song.title}
              </h3>
              <p className="text-zinc-500 mb-4">{song.artist}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {song.chords.slice(0, 4).map((chord, i) => (
                  <span key={i} className="px-2 py-1 bg-white/5 text-zinc-400 text-xs rounded">
                    {chord}
                  </span>
                ))}
                {song.chords.length > 4 && (
                  <span className="px-2 py-1 bg-white/5 text-zinc-400 text-xs rounded">
                    +{song.chords.length - 4}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm">
                  <span className={`px-2 py-1 rounded-full ${
                    song.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                    song.difficulty === 'intermediate' ? 'bg-blue/20 text-blue' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {t(`levels.${song.difficulty}`)}
                  </span>
                  <span className="text-zinc-500">{song.bpm} BPM</span>
                </div>
              </div>

              {(!song.is_premium || isPremium) && (
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => openSong(song)}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-400 text-black font-semibold"
                    data-testid={`view-song-${song.id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    View Song
                  </Button>

                  {practicedIds.has(song.id) ? (
                    <Button
                      disabled
                      className="bg-green-500/20 text-green-400 cursor-default"
                      data-testid={`practiced-${song.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => markPracticed(song.id)}
                      disabled={savingId === song.id}
                      variant="outline"
                      className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                      data-testid={`mark-practiced-${song.id}`}
                    >
                      {savingId === song.id ? (
                        <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Guitar className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </motion.div>
          );

          if (groupedChapters) {
            return (
              <div className="space-y-4">
                {groupedChapters.map((chapter, chapterIdx) => {
                  const open = !!openChapters[chapter.difficulty];
                  return (
                    <section key={chapter.difficulty} data-testid={`chapter-${chapter.difficulty}`}>
                      <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: chapterIdx * 0.04 }}
                        onClick={() => toggleChapter(chapter.difficulty)}
                        aria-expanded={open}
                        className={`w-full flex items-baseline justify-between gap-3 border-b border-white/5 pb-3 text-left hover:border-green-500/30 transition-colors ${open ? 'mb-4' : ''}`}
                      >
                        <div className="flex items-baseline gap-3">
                          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                            {t(`levels.${chapter.difficulty}`)}
                          </h2>
                          <span className="text-zinc-500 text-sm font-mono">
                            {chapter.items.length}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-zinc-500 transition-transform duration-200 self-center ${open ? '' : '-rotate-90'}`}
                        />
                      </motion.button>
                      {open && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {chapter.items.map((song, i) => renderCard(song, i))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSongs.map((song, index) => renderCard(song, index))}
            </div>
          );
        })()}

        {/* Song Detail Modal */}
        {selectedSong && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedSong(null)}
          >
            <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#121212] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-3xl w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white">
                    {selectedSong.title}
                  </h2>
                  <p className="text-zinc-500">{selectedSong.artist}</p>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    selectedSong.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                    selectedSong.difficulty === 'intermediate' ? 'bg-blue/20 text-blue' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {t(`levels.${selectedSong.difficulty}`)}
                  </span>
                  <p className="text-zinc-500 text-sm mt-2">{selectedSong.bpm} BPM</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-zinc-500 text-sm mb-2">{t('songs.chordsUsed')}:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedSong.chords.map((chord, i) => (
                    <span key={i} className="px-3 py-1 bg-amber/10 text-amber rounded-full text-sm font-medium">
                      {chord}
                    </span>
                  ))}
                </div>
              </div>

              {/* Practice toolbar: auto-scroll + accompaniment metronome */}
              {!songLoading && selectedSong.lyrics_with_chords && (
                <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-3 mb-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={() => setAutoScroll(v => !v)}
                      size="sm"
                      className={autoScroll
                        ? 'bg-amber text-black hover:bg-amber/90'
                        : 'bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/10'}
                      data-testid="song-autoscroll-toggle"
                    >
                      {autoScroll ? <Pause className="w-4 h-4 mr-1.5 fill-current" /> : <Play className="w-4 h-4 mr-1.5 fill-current" />}
                      {t('songs.autoScrollShort')}
                    </Button>
                    <Button
                      onClick={() => setShowMetronome(v => !v)}
                      size="sm"
                      variant="outline"
                      className={showMetronome
                        ? 'border-amber text-amber hover:bg-amber/10'
                        : 'border-white/15 text-zinc-300 hover:bg-white/5'}
                      data-testid="song-metronome-toggle"
                    >
                      <Timer className="w-4 h-4 mr-1.5" />
                      {t('songs.metronome')}
                      <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${showMetronome ? 'bg-amber/20 text-amber' : 'bg-white/5 text-zinc-500'}`}>
                        {showMetronome ? 'ON' : 'OFF'}
                      </span>
                    </Button>
                    <Button
                      onClick={() => setShowDrums(v => !v)}
                      size="sm"
                      variant="outline"
                      className={showDrums
                        ? 'border-amber text-amber hover:bg-amber/10'
                        : 'border-white/15 text-zinc-300 hover:bg-white/5'}
                      data-testid="song-drums-toggle"
                    >
                      <Music className="w-4 h-4 mr-1.5" />
                      {t('songs.drums')}
                      <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${showDrums ? 'bg-amber/20 text-amber' : 'bg-white/5 text-zinc-500'}`}>
                        {showDrums ? 'ON' : 'OFF'}
                      </span>
                    </Button>
                    <Button
                      onClick={() => setShowBacking(v => !v)}
                      size="sm"
                      variant="outline"
                      className={showBacking
                        ? 'border-amber text-amber hover:bg-amber/10'
                        : 'border-white/15 text-zinc-300 hover:bg-white/5'}
                      data-testid="song-backing-toggle"
                    >
                      <Music className="w-4 h-4 mr-1.5" />
                      {t('songs.backingBand', { defaultValue: 'Backing band' })}
                      <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${showBacking ? 'bg-amber/20 text-amber' : 'bg-white/5 text-zinc-500'}`}>
                        {showBacking ? 'ON' : 'OFF'}
                      </span>
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <span className="shrink-0">{t('songs.scrollSpeed')}</span>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.05"
                      value={autoScrollSpeed}
                      onChange={(e) => setAutoScrollSpeed(parseFloat(e.target.value))}
                      className="flex-1 accent-amber"
                    />
                    <span className="font-mono text-amber w-10 text-right shrink-0">{autoScrollSpeed.toFixed(2)}x</span>
                  </div>
                  {/* View mode switch: lyrics-with-chords vs tab + diagrams */}
                  <div className="flex items-center gap-1 bg-[#121212] border border-white/10 rounded-lg p-1 self-start">
                    <button
                      onClick={() => setViewMode('chords')}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        viewMode === 'chords' ? 'bg-amber text-black' : 'text-zinc-400 hover:text-white'
                      }`}
                      data-testid="song-view-chords"
                    >
                      {t('songs.viewChords')}
                    </button>
                    <button
                      onClick={() => setViewMode('tab')}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        viewMode === 'tab' ? 'bg-amber text-black' : 'text-zinc-400 hover:text-white'
                      }`}
                      data-testid="song-view-tab"
                    >
                      {t('songs.viewTab')}
                    </button>
                  </div>
                  {showMetronome && (
                    <MiniMetronome
                      initialBpm={selectedSong.bpm || 90}
                      bpmMin={40}
                      bpmMax={240}
                      accent="amber"
                    />
                  )}
                  {showDrums && (
                    <MiniDrums
                      initialBpm={selectedSong.bpm || 90}
                      initialPattern={guessBackingStyles(selectedSong).drums}
                      accent="amber"
                    />
                  )}
                  {showBacking && (() => {
                    if (selectedSong.audio_stems && Object.keys(selectedSong.audio_stems).length > 0) {
                      return (
                        <AudioBackingTrack
                          stems={selectedSong.audio_stems}
                          attribution={selectedSong.audio_attribution}
                          accent="amber"
                        />
                      );
                    }
                    const styles = guessBackingStyles(selectedSong);
                    return (
                      <BackingTrack
                        bpm={selectedSong.bpm || 90}
                        timeSignature={selectedSong.time_signature || '4/4'}
                        chords={
                          (selectedSong.structure && selectedSong.structure[0] && selectedSong.structure[0].chords)
                            ? selectedSong.structure[0].chords
                            : selectedSong.chords
                        }
                        initialPattern={styles.drums}
                        bassStyle={styles.bass}
                        pianoStyle={styles.piano}
                        accent="amber"
                      />
                    );
                  })()}
                </div>
              )}

              <div
                ref={lyricsScrollRef}
                className="bg-[#0A0A0A] rounded-lg p-6 overflow-y-auto max-h-[60vh]"
              >
                {songLoading ? (
                  <div className="flex justify-center py-10">
                    <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : viewMode === 'chords' ? (
                  <LyricsWithChords>{selectedSong.lyrics_with_chords}</LyricsWithChords>
                ) : (
                  <div>
                    <p className="text-zinc-400 text-sm mb-4 italic">
                      {t('songs.tabIntro')}
                    </p>
                    {selectedSong.tab_sections && Object.keys(selectedSong.tab_sections).length > 0 ? (
                      <div className="space-y-6">
                        {Object.entries(selectedSong.tab_sections).map(([sectionName, tabText]) => (
                          <div key={sectionName}>
                            <p className="text-zinc-300 text-sm font-semibold mb-2 capitalize">
                              {sectionName.replace(/_/g, ' ')}
                            </p>
                            <TabNotation>{tabText}</TabNotation>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 text-center">
                        <p className="text-zinc-400 text-sm mb-2">
                          {t('songs.noTabAvailable')}
                        </p>
                        <p className="text-zinc-500 text-xs">
                          {t('songs.noTabFallback')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                variant="ghost" 
                className="w-full mt-6 text-zinc-400"
                onClick={() => setSelectedSong(null)}
              >
                {t('common.back')}
              </Button>
            </motion.div>
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && filteredSongs.length === 0 && (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No songs found with current filters</p>
          </div>
        )}
      </div>

      {/* Library Picker Modal */}
      {showLibPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lib-picker-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowLibPicker(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <Music className="w-5 h-5 text-purple-400" />
                Aggiungi dalla Libreria
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLibPicker(false)}
                className="text-zinc-400 hover:text-white"
                data-testid="lib-picker-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={libSearchQ}
                onChange={(e) => setLibSearchQ(e.target.value)}
                placeholder={t('songs.searchPlaceholder')}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-purple-500/50"
                data-testid="lib-picker-search"
              />
            </div>

            {/* Song List */}
            <div className="flex-1 overflow-y-auto space-y-1 mb-4 min-h-0">
              {savedSongs
                .filter(s => !libSearchQ || s.name.toLowerCase().includes(libSearchQ.toLowerCase()))
                .map(s => {
                  const isChecked = libPickerSel.has(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleLibPickerItem(s.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                        isChecked ? 'bg-purple-500/15 border border-purple-500/30' : 'bg-white/3 hover:bg-white/5 border border-transparent'
                      }`}
                      data-testid={`lib-pick-${s.id}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isChecked ? 'bg-purple-500 border-purple-500' : 'border-zinc-600'
                      }`}>
                        {isChecked && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{s.name}</p>
                        <p className="text-zinc-500 text-xs">{fmtDate(s.createdAt)}</p>
                      </div>
                    </button>
                  );
                })}
              {savedSongs.filter(s => !libSearchQ || s.name.toLowerCase().includes(libSearchQ.toLowerCase())).length === 0 && (
                <p className="text-zinc-500 text-sm text-center py-4">
                  {savedSongs.length === 0 ? t('songs.emptyLibrary') : t('songs.noResults')}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <span className="text-zinc-500 text-sm">
                {libPickerSel.size} selezionati
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowLibPicker(false)}
                  className="text-zinc-400"
                >
                  Chiudi
                </Button>
                <Button
                  onClick={addFromLibrary}
                  size="sm"
                  disabled={libPickerSel.size === 0}
                  className="bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-40"
                  data-testid="lib-picker-add"
                >
                  Aggiungi al Multitrack
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SongsPage;
